import { useEffect, useState } from 'react';
import { Download, X, Smartphone, Share } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { BottomSheet } from '@/components/common/BottomSheet';

const DISMISS_KEY = 'attendance_pwa_install_dismissed';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 hari

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
    return (window.matchMedia?.('(display-mode: standalone)').matches) ||
           (window.navigator as any).standalone === true;
}

function isIos(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function dismissedRecently(): boolean {
    try {
        const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        return Date.now() - ts < DISMISS_TTL_MS;
    } catch { return false; }
}

export function InstallPwaPrompt() {
    const { t } = useI18n();
    const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
    const [showIos, setShowIos] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (isStandalone()) return; // sudah ter-install
        if (dismissedRecently()) return;

        const handler = (e: Event) => {
            e.preventDefault();
            setDeferred(e as BeforeInstallPromptEvent);
            // Tunggu user interact 1x sebelum prompt
            setTimeout(() => setOpen(true), 8_000);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // iOS: tidak fire beforeinstallprompt — show manual guide
        if (isIos() && !isStandalone()) {
            setTimeout(() => { setShowIos(true); setOpen(true); }, 8_000);
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
        setOpen(false);
    };

    const install = async () => {
        if (!deferred) return;
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === 'accepted') {
            try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 365 * 24 * 60 * 60 * 1000)); } catch {}
        }
        setDeferred(null);
        setOpen(false);
    };

    if (!open) return null;

    if (showIos) {
        return (
            <BottomSheet open={open} onOpenChange={(o) => { if (!o) dismiss(); }} title={t('pwa.ios_title')}>
                <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <Share className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-700">Step 1</p>
                            <p className="text-sm">{t('pwa.ios_step1')}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                            <Smartphone className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-700">Step 2</p>
                            <p className="text-sm">{t('pwa.ios_step2')}</p>
                        </div>
                    </div>
                    <Button onClick={dismiss} variant="outline" className="w-full">{t('common.close')}</Button>
                </div>
            </BottomSheet>
        );
    }

    return (
        <BottomSheet open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
            <div className="text-center py-4">
                <div className="h-16 w-16 mx-auto rounded-2xl bg-brand flex items-center justify-center mb-4">
                    <Download className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-base font-black text-gray-900 mb-1">{t('pwa.install_title')}</h2>
                <p className="text-sm text-gray-500 mb-6 max-w-[280px] mx-auto">{t('pwa.install_body')}</p>
                <div className="flex gap-2">
                    <Button onClick={dismiss} variant="ghost" className="flex-1">{t('pwa.install_dismiss')}</Button>
                    <Button onClick={install} className="flex-1">
                        <Download className="h-4 w-4 mr-1.5" />
                        {t('pwa.install_btn')}
                    </Button>
                </div>
            </div>
        </BottomSheet>
    );
}
